/**
 * RTWO Asset & Token Engine — Token Lifecycle Service
 */

import type { AssetId, RTWOAssetLifecycleStage, RTWOAssetRegistryRecord } from '../domain-models';
import type { ITokenLifecycleService, TokenLifecycleResult } from '../service-interfaces';
import type { ASSRepositories } from '../repositories/ass-repositories';
import type {
  LedgerAdapterRegistry,
  ScrtRuntimeAdapterRegistry,
  VmRuntimeAdapterRegistry,
} from '../registry/adapter-registries';
import { ASSTInternalEventTypes } from '../events/ass-event-bus';
import { BaseASSService, type ASSServiceContext } from './base-ass-service';

export class TokenLifecycleService extends BaseASSService implements ITokenLifecycleService {
  readonly serviceId = 'ass-token-lifecycle-service-001';
  readonly serviceName = 'Token Lifecycle Service';
  readonly description =
    'Token lifecycle — placeholder, requires real Ledger Connection and Token Runtime. No fake lifecycle transitions. No fake minting, burning, or transfers.';
  readonly integrations = [
    'Core Engine',
    'Ledger Engine',
    'Smart Contract Runtime',
    'VM Sandbox',
    'Event Bus',
  ];
  readonly requiresLedgerConnection = true;
  readonly requiresTokenRuntime = true;

  constructor(
    context: ASSServiceContext,
    private readonly repositories: ASSRepositories,
    private readonly ledgerRegistry: LedgerAdapterRegistry,
    private readonly scrtRegistry: ScrtRuntimeAdapterRegistry,
    private readonly vmRegistry: VmRuntimeAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_token_runtime';
  }

  async transitionLifecycle(
    assetId: AssetId,
    targetStage: RTWOAssetLifecycleStage
  ): Promise<TokenLifecycleResult> {
    if (
      !this.ledgerRegistry.isConfigured() ||
      !this.scrtRegistry.isConfigured() ||
      !this.vmRegistry.isConfigured()
    ) {
      return {
        success: false,
        lifecycleId: null,
        error:
          'Token lifecycle transitions require real Ledger Connection and Token Runtime — Not Operational',
        isPlaceholder: true,
        source: 'Asset & Token Engine — Token Lifecycle Not Operational',
      };
    }

    const scrtResult = await this.scrtRegistry
      .getActiveAdapter()
      .transitionLifecycle(assetId, targetStage);

    if (scrtResult?.record) {
      this.repositories.registryRecords.save(scrtResult.record);
      this.context.eventBus.emit(ASSTInternalEventTypes.LIFECYCLE_TRANSITIONED, {
        recordId: scrtResult.record.recordId,
        assetId,
      });
    }

    const vmResult = await this.vmRegistry
      .getActiveAdapter()
      .transitionLifecycle(assetId, targetStage);

    if (vmResult?.record) {
      this.repositories.registryRecords.save(vmResult.record);
    }

    return {
      success: false,
      lifecycleId: null,
      error:
        'Token lifecycle transitions require real Ledger Connection and Token Runtime — Not Operational',
      isPlaceholder: true,
      source: 'Asset & Token Engine — Token Lifecycle Not Operational',
    };
  }

  getLifecycleStage(_assetId: AssetId): RTWOAssetLifecycleStage | null {
    return null;
  }

  getRegistryRecord(assetId: AssetId): RTWOAssetRegistryRecord | null {
    if (!this.ledgerRegistry.isConfigured()) return null;
    return this.repositories.registryRecords.findByAssetId(assetId);
  }

  getAllRegistryRecords(): RTWOAssetRegistryRecord[] {
    if (!this.ledgerRegistry.isConfigured()) return [];
    return this.repositories.registryRecords.findAll();
  }

  getRegistryRecordCount(): 0 {
    return 0;
  }
}
